"use client";

import { createContext, useCallback, useContext, useEffect } from "react";

import isEqual from "lodash/isEqual";

import {
  DEFAULT_FILTERS,
  colorByOptions,
  resolvePlottingColorByDefault,
} from "@/app/(internal)/panel/(maps)/_constants/plotting-filters.constants";
import {
  areSavedPlottingFiltersValid,
  buildFiltersFromPresetSnapshot,
  computeDefaultFilters,
} from "@/app/(internal)/panel/(maps)/_utils/plotting-filters.utils";
import { usePlottingFiltersData } from "@/providers/maps/contexts/plotting-filters/use-plotting-filters-data";
import { usePlottingFiltersLayer } from "@/providers/maps/contexts/plotting-filters/use-plotting-filters-layer";
import { usePlottingFiltersState } from "@/providers/maps/contexts/plotting-filters/use-plotting-filters-state";
import { usePlottingFiltersStorage } from "@/providers/maps/contexts/plotting-filters/use-plotting-filters-storage";
import type {
  PlottingFiltersContextValue,
  PlottingFiltersProviderProps,
  SavedPlottingFilters,
} from "@/providers/maps/contexts/plotting-filters-context.types";
import type { ISchoolColorPaletteResponse } from "@/providers/store/api/districts/districts.interface";

const PlottingFiltersContext = createContext<PlottingFiltersContextValue | null>(null);

export function usePlottingFilters() {
  const ctx = useContext(PlottingFiltersContext);
  if (!ctx) throw new Error("usePlottingFilters must be used inside <PlottingFiltersProvider />");
  return ctx;
}

export function PlottingFiltersProvider({ children }: PlottingFiltersProviderProps) {
  const data = usePlottingFiltersData();
  const {
    selectedDistrictId,
    districtMetadata,
    schoolColorPalette,
    fulfilledTimeStamp,
    prevFulfilledTimeStamp,
    prevDistrictIdRef,
    importPeriodOptions,
    schoolTypeOptions,
    gradeOptions,
    gradeColorMap,
    districtSchools,
    isLoadingMetadata,
    isLoadingColorPalette,
    hasStudentDataProcessing,
  } = data;

  const { currentDistrictFilters, savedFilters, setSavedFilters } = usePlottingFiltersStorage(
    selectedDistrictId,
    isLoadingMetadata,
    fulfilledTimeStamp,
  );

  const state = usePlottingFiltersState(
    selectedDistrictId,
    importPeriodOptions,
    schoolTypeOptions,
    gradeOptions,
  );
  const {
    filters,
    setFiltersState,
    colorBy,
    setColorByState,
    isApplied,
    setIsApplied,
    hasSyncedWithBackend,
    setHasSyncedWithBackend,
    resetKey,
    bumpResetKey,
    setFilters,
    setColorBy,
    applyFilters,
    resetFilters,
  } = state;

  usePlottingFiltersLayer(isApplied);

  useEffect(() => {
    if (!districtMetadata || isLoadingMetadata || !fulfilledTimeStamp) {
      setHasSyncedWithBackend(false);
      return;
    }

    const districtChanged =
      !prevDistrictIdRef.current || prevDistrictIdRef.current !== selectedDistrictId;
    const dataWasRefetched =
      !prevFulfilledTimeStamp || prevFulfilledTimeStamp !== fulfilledTimeStamp;
    const shouldSync = districtChanged || dataWasRefetched;

    if (shouldSync) {
      const savedValid =
        currentDistrictFilters?.isApplied &&
        currentDistrictFilters.filters &&
        areSavedPlottingFiltersValid(
          currentDistrictFilters.filters,
          importPeriodOptions,
          schoolTypeOptions,
          gradeOptions,
          districtSchools,
        );

      if (savedValid) {
        setFiltersState(buildFiltersFromPresetSnapshot(currentDistrictFilters.filters));
        setIsApplied(currentDistrictFilters.isApplied ?? false);
      } else {
        setFiltersState({
          ...DEFAULT_FILTERS,
          ...computeDefaultFilters(
            DEFAULT_FILTERS,
            importPeriodOptions,
            schoolTypeOptions,
            gradeOptions,
            districtSchools,
          ),
        });

        setIsApplied(false);
        if (selectedDistrictId && currentDistrictFilters) {
          setSavedFilters((prev) => {
            if (!prev?.[selectedDistrictId]) return prev ?? null;
            const next = { ...prev };
            delete next[selectedDistrictId];
            return Object.keys(next).length > 0 ? next : null;
          });
        }
      }

      setColorByState(resolvePlottingColorByDefault(currentDistrictFilters?.colorBy));
      setHasSyncedWithBackend(true);
      // Signal the plotting-filters form to reset to the newly synced values.
      bumpResetKey();
    }

    prevDistrictIdRef.current = selectedDistrictId;
  }, [districtMetadata, currentDistrictFilters, importPeriodOptions, schoolTypeOptions, gradeOptions, districtSchools, selectedDistrictId, fulfilledTimeStamp, prevFulfilledTimeStamp, isLoadingMetadata, prevDistrictIdRef, setFiltersState, setIsApplied, setColorByState, setHasSyncedWithBackend, setSavedFilters, bumpResetKey]);

  useEffect(() => {
    const districtChanged =
      !prevDistrictIdRef.current || prevDistrictIdRef.current !== selectedDistrictId;
    const dataWasRefetched =
      !prevFulfilledTimeStamp || prevFulfilledTimeStamp !== fulfilledTimeStamp;

    if (
      !selectedDistrictId ||
      isLoadingMetadata ||
      districtChanged ||
      dataWasRefetched
    )
      return;

    setSavedFilters((prev) => {
      const prevEntry = prev?.[selectedDistrictId];

      const same =
        prevEntry &&
        isEqual(prevEntry.filters, filters) &&
        prevEntry.isApplied === isApplied &&
        prevEntry.colorBy === colorBy;

      if (same) return prev;

      return {
        ...(prev ?? {}),
        [selectedDistrictId]: { filters, isApplied, colorBy },
      };
    });
  }, [
    selectedDistrictId,
    filters,
    isApplied,
    colorBy,
    isLoadingMetadata,
    setSavedFilters,
    prevFulfilledTimeStamp,
    fulfilledTimeStamp,
    currentDistrictFilters?.colorBy,
    prevDistrictIdRef,
  ]);

  const setStoredForPreset = useCallback(
    (patch: Record<string, SavedPlottingFilters>) => {
      if (!selectedDistrictId) return;

      const preset = patch[selectedDistrictId];
      if (!preset) return;

      const nextColorBy = resolvePlottingColorByDefault(preset.colorBy);
      let nextFilters = buildFiltersFromPresetSnapshot(preset.filters);

      if (!preset.isApplied) {
        nextFilters = {
          ...nextFilters,
          ...computeDefaultFilters(
            nextFilters,
            importPeriodOptions,
            schoolTypeOptions,
            gradeOptions,
            districtSchools,
          ),
          advancedFilters: [],
        };
      }

      setFiltersState(nextFilters);
      setColorByState(nextColorBy);
      setIsApplied(preset.isApplied);
      setSavedFilters((prev) => ({
        ...(prev ?? {}),
        [selectedDistrictId]: {
          filters: nextFilters,
          isApplied: preset.isApplied,
          colorBy: nextColorBy,
        },
      }));
      bumpResetKey();
    },
    [
      selectedDistrictId,
      importPeriodOptions,
      schoolTypeOptions,
      gradeOptions,
      districtSchools,
      setFiltersState,
      setColorByState,
      setIsApplied,
      setSavedFilters,
      bumpResetKey,
    ],
  );

  const contextValue: PlottingFiltersContextValue = {
    filters,
    isApplied,
    setFilters,
    applyFilters,
    resetFilters,
    resetKey,
    importPeriodOptions,
    schoolTypeOptions,
    gradeOptions,
    colorByOptions,
    isLoadingMetadata,
    schoolColorPalette: (schoolColorPalette as ISchoolColorPaletteResponse | undefined)?.schools,
    gradeColorMap,
    colorBy,
    setColorBy,
    isLoadingColorPalette,
    hasSyncedWithBackend,
    hasStudentDataProcessing,
    savedFilters,
    setSavedFilters,
    setStoredForPreset,
  };

  return (
    <PlottingFiltersContext.Provider value={contextValue}>
      {children}
    </PlottingFiltersContext.Provider>
  );
}
